import { type AuthRequest } from 'expo-auth-session';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * NOTE: We are not using `makeAuthUrlAsync`: it encodes the scope separator as `+`,
 *       which ID.me rejects.
 */
export function buildAuthorizeUrl(params: Params) {
  const { authorizationEndpoint, config } = params;
  const { clientId, redirectUri, responseType, state, scopes, prompt, clientSecret } = config;
  const { codeChallenge, codeChallengeMethod, usePKCE } = config;

  if (!state) {
    throw new Error('Cannot build an ID.me authorize URL without a state value');
  }

  const hasChallengeMethod = Boolean(usePKCE && codeChallengeMethod);
  const hasScopes = scopes !== undefined && scopes.length > 0;

  const extraParams = getExtraParams(config);
  const searchParams = new URLSearchParams(extraParams);

  if (codeChallenge) {
    searchParams.set('code_challenge', codeChallenge);
  }

  if (hasChallengeMethod) {
    searchParams.set('code_challenge_method', String(codeChallengeMethod));
  }

  if (clientSecret) {
    searchParams.set('client_secret', clientSecret);
  }

  if (prompt) {
    searchParams.set('prompt', Array.isArray(prompt) ? prompt.join(' ') : String(prompt));
  }

  searchParams.set('redirect_uri', redirectUri);
  searchParams.set('client_id', clientId);
  searchParams.set('response_type', String(responseType ?? 'code'));
  searchParams.set('state', state);

  if (hasScopes) {
    searchParams.set('scope', scopes.join(SCOPE_DELIMITER));
  }

  return `${authorizationEndpoint}?${searchParams}`;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getExtraParams(config: AuthRequestConfig) {
  const entries = Object.entries(config.extraParams ?? {});

  return Object.fromEntries(entries.filter(checkIfEntryHasValue).map(toStringEntry));
}

function checkIfEntryHasValue(entry: ParamEntry) {
  const [, value] = entry;

  return value !== null && value !== undefined;
}

function toStringEntry(entry: ParamEntry): [string, string] {
  const [key, value] = entry;

  return [key, String(value)];
}

// ─── Constants ───────────────────────────────────────────────────────────────

// ID.me support says commas, its docs say spaces; neither works for our client yet.
const SCOPE_DELIMITER = ',';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  authorizationEndpoint: string;
  config: AuthRequestConfig;
};

type AuthRequestConfig = Awaited<ReturnType<AuthRequest['getAuthRequestConfigAsync']>>;

type ParamEntry = [string, unknown];
